import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import TicketSelector from '../components/TicketSelector';
import Skeleton from '../components/Skeleton';
import { trackEventViewed, trackCheckoutStarted } from '../utils/analytics';
import { getLocale } from '../utils/locale';
import { formatPrice } from '../utils/format';

export default function EventDetail() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [selectedTickets, setSelectedTickets] = useState([]);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.getEvent(id)
      .then(eventData => {
        setEvent(eventData);
        // Track event view for analytics
        trackEventViewed(eventData);
      })
      .catch(err => setFetchError(err.message))
      .finally(() => setLoading(false));
  }, [id]);

  const isSeatedEvent = event?.venueLayout?.type === 'seated';

  // Calculate urgency/scarcity metrics
  const urgencyData = useMemo(() => {
    if (!event?.tickets || event.tickets.length === 0) return null;

    const total = event.tickets.reduce((s, ticket) => s + ticket.quantity, 0);
    const sold = event.tickets.reduce((s, ticket) => s + (ticket.sold || 0), 0);
    const available = total - sold;
    const percentSold = (sold / total) * 100;

    return {
      available,
      percentSold,
      isLowStock: available <= 20 && available > 0,
      isAlmostSoldOut: percentSold >= 80,
      isSoldOut: available === 0,
    };
  }, [event]);

  // Check if event is happening soon
  const eventTiming = useMemo(() => {
    if (!event?.date) return null;

    const eventDate = new Date(event.date);
    const now = new Date();
    const daysUntil = Math.ceil((eventDate - now) / (1000 * 60 * 60 * 24));

    return {
      daysUntil,
      isThisWeek: daysUntil <= 7 && daysUntil > 0,
      isTomorrow: daysUntil === 1,
      isToday: daysUntil === 0,
      isPast: daysUntil < 0,
    };
  }, [event]);

  const handleCheckout = async () => {
    if (!email) {
      setError(t('eventDetail.errorEmail'));
      return;
    }
    if (selectedTickets.length === 0) {
      setError(t('eventDetail.errorSelectTicket'));
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      // Track checkout started
      trackCheckoutStarted({
        eventId: event.id,
        tickets: selectedTickets,
        email,
      });

      const result = await api.createCheckoutSession({
        eventId: event.id,
        tickets: selectedTickets,
        email,
      });
      const resumedParam = result.resumed ? '&resumed=1' : '';
      navigate(`/checkout/pay?orderId=${result.sessionId}&email=${encodeURIComponent(email)}${resumedParam}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Skeleton.EventDetail />;
  }

  if (fetchError) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{t('common.errorLoading')}</p>
        <button onClick={() => window.location.reload()} className="btn-secondary">
          {t('common.retry')}
        </button>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">{t('eventDetail.notFound')}</h2>
        <p className="text-gray-500 mb-6">{t('eventDetail.notFoundDesc')}</p>
        <Link to="/" className="btn-primary">{t('eventDetail.browseEvents')}</Link>
      </div>
    );
  }

  const eventDate = new Date(event.date);

  // Get pricing info for seated events
  const getSeatPricing = () => {
    if (!isSeatedEvent) return null;
    const sections = event.venueLayout.sections || [];
    if (sections.length === 0) return null;

    const prices = sections.map(s => s.price);
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);

    return { minPrice, maxPrice, sectionCount: sections.length };
  };

  const seatPricing = getSeatPricing();

  return (
    <div className="max-w-4xl mx-auto">
      {/* Hero Banner */}
      <div className="relative bg-gradient-to-br from-brand-500 to-brand-700 rounded-2xl h-72 flex items-center justify-center mb-8 overflow-hidden">
        <span className="text-white/20 text-9xl font-bold select-none">
          {event.title.charAt(0)}
        </span>

        {/* Urgency Badges */}
        <div className="absolute top-4 left-4 flex flex-wrap gap-2">
          {eventTiming?.isToday && (
            <span className="bg-red-500 text-white text-xs font-bold px-3 py-1.5 rounded-full animate-pulse">
              {t('eventDetail.today')}
            </span>
          )}
          {eventTiming?.isTomorrow && (
            <span className="bg-amber-500 text-white text-xs font-bold px-3 py-1.5 rounded-full">
              {t('eventDetail.tomorrow')}
            </span>
          )}
          {eventTiming?.isThisWeek && !eventTiming?.isTomorrow && !eventTiming?.isToday && (
            <span className="bg-brand-400 text-white text-xs font-bold px-3 py-1.5 rounded-full">
              {t('eventDetail.thisWeek')}
            </span>
          )}
          {urgencyData?.isAlmostSoldOut && !urgencyData?.isSoldOut && (
            <span className="bg-red-500 text-white text-xs font-bold px-3 py-1.5 rounded-full animate-pulse">
              {urgencyData.percentSold.toFixed(0)}% SOLD
            </span>
          )}
          {urgencyData?.isLowStock && !urgencyData?.isAlmostSoldOut && (
            <span className="bg-amber-500 text-white text-xs font-bold px-3 py-1.5 rounded-full">
              {t('eventDetail.onlyLeft', { count: urgencyData.available })}
            </span>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        {/* Event Details */}
        <div className="md:col-span-2">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">{event.title}</h1>

          {/* Key Info Cards */}
          <div className="flex flex-wrap gap-4 mb-6">
            <div className="flex items-center gap-3 bg-gray-50 px-4 py-3 rounded-xl">
              <div className="w-10 h-10 bg-brand-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('eventDetail.dateTime')}</p>
                <p className="font-medium text-gray-900">
                  {eventDate.toLocaleDateString(getLocale(i18n.language), {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                  })} at {eventDate.toLocaleTimeString(getLocale(i18n.language), {
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                </p>
              </div>
            </div>

            {event.venue && (
              <div className="flex items-center gap-3 bg-gray-50 px-4 py-3 rounded-xl">
                <div className="w-10 h-10 bg-brand-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t('eventDetail.location')}</p>
                  <p className="font-medium text-gray-900">{event.venue}</p>
                </div>
              </div>
            )}
          </div>

          {/* Organizer */}
          {event.organizer && (
            <div className="flex items-center gap-3 mb-6 p-4 bg-gray-50 rounded-xl">
              <div className="w-12 h-12 bg-brand-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                {event.organizer.businessName.charAt(0)}
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('eventDetail.presentedBy')}</p>
                <p className="font-medium text-gray-900">{event.organizer.businessName}</p>
              </div>
            </div>
          )}

          {/* Description */}
          {event.description && (
            <div className="prose prose-gray max-w-none">
              <h3 className="text-lg font-semibold mb-3">{t('eventDetail.aboutEvent')}</h3>
              <p className="text-gray-600 whitespace-pre-wrap leading-relaxed">{event.description}</p>
            </div>
          )}

          {/* Trust Signals */}
          <div className="mt-8 pt-6 border-t border-gray-100">
            <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                {t('eventDetail.secureCheckout')}
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                {t('eventDetail.instantConfirmation')}
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                {t('eventDetail.mobileTickets')}
              </div>
            </div>
          </div>
        </div>

        {/* Ticket Selection Panel */}
        <div className="card p-6 h-fit sticky top-24">
          {isSeatedEvent ? (
            // Seated Event - Link to seat selection
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('eventDetail.getTickets')}</h3>

              <div className="mb-4">
                <span className="inline-flex items-center gap-1.5 text-sm bg-brand-100 text-brand-700 px-3 py-1.5 rounded-lg font-medium">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                  </svg>
                  {t('eventCard.reservedSeating')}
                </span>
              </div>

              {seatPricing && (
                <div className="space-y-2 mb-6">
                  <div className="text-3xl font-bold text-gray-900">
                    {seatPricing.minPrice === seatPricing.maxPrice
                      ? formatPrice(seatPricing.minPrice)
                      : `${formatPrice(seatPricing.minPrice)} - ${formatPrice(seatPricing.maxPrice)}`}
                  </div>
                  <p className="text-sm text-gray-500">
                    {seatPricing.sectionCount === 1
                      ? t('eventDetail.sectionsAvailable', { count: seatPricing.sectionCount })
                      : t('eventDetail.sectionsAvailable_plural', { count: seatPricing.sectionCount })}
                  </p>
                </div>
              )}

              <Link
                to={`/event/${event.id}/seats`}
                className="btn-primary w-full py-3 text-center block"
              >
                {t('eventDetail.selectYourSeats')}
              </Link>

              <p className="text-xs text-gray-400 text-center mt-3">
                {t('eventDetail.seatingChartHint')}
              </p>
            </div>
          ) : (
            // GA Event - Ticket selector
            <>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('eventDetail.selectTickets')}</h3>

              <TicketSelector
                tickets={event.tickets}
                onSelect={setSelectedTickets}
              />

              {selectedTickets.length > 0 && (
                <div className="mt-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      {t('eventDetail.emailForTickets')}
                    </label>
                    <input
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="input-field"
                    />
                  </div>

                  {error && (
                    <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 px-3 py-2 rounded-lg">
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {error}
                    </div>
                  )}

                  <button
                    onClick={handleCheckout}
                    disabled={submitting}
                    className="btn-primary w-full py-3"
                  >
                    {submitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        {t('common.processing')}
                      </span>
                    ) : (
                      t('eventDetail.continueToCheckout')
                    )}
                  </button>

                  <p className="text-xs text-gray-400 text-center">
                    {t('eventDetail.noChargeUntilConfirm')}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
